import React, { FunctionComponent, ImgHTMLAttributes } from "react";

export interface IResponsiveImageProps {
  sizes?: string;
  srcSet: string;
}

export type SourceProps = IResponsiveImageProps &
  (
    | {
        media: string;
        type?: string;
      }
    | {
        media?: string;
        type: string;
      }
  );

export type ImageDataSources = {
  sources?: SourceProps[];
  fallback?: FallbackProps;
};

export type FallbackProps = { src: string } & Partial<IResponsiveImageProps>;

type ImageProps = ImgHTMLAttributes<HTMLImageElement> & {
  src?: string;
  alt: string;
  shouldLoad: boolean;
  aspectRadio?: string;
  outputPixelDensities?: number[];
};

export type PictureProps = ImgHTMLAttributes<HTMLImageElement> & {
  fallback?: FallbackProps;
  sources?: Array<SourceProps>;
  alt: string;
  shouldLoad?: boolean;
  aspectRadio?: string;
  outputPixelDensities?: number[];
  width?: number;
  height?: number;
  log?: boolean;
};

const Image: FunctionComponent<ImageProps> = function Image({
  src,
  srcSet,
  loading,
  alt = ``,
  shouldLoad,
  aspectRadio = "auto",
  style,
  ...props
}) {
  const { width, height } = props;
  const w = Number(width),
    h = Number(height);
  const aspectRatioFromDimensions = w > 0 && h > 0 ? w / h : undefined;
  const resolvedAspectRatio =
    aspectRatioFromDimensions ?? style?.aspectRatio ?? aspectRadio ?? "auto";
  // string: React/inline OK, mais cohérent avec wrapper CSS (évite 0.666px)
  const aspectRatioCss =
    typeof resolvedAspectRatio === "number"
      ? String(resolvedAspectRatio)
      : resolvedAspectRatio;
  // FETCHPRIORITY-FIX: `fetchPriority` (camelCase) arrive via prop-drilling
  // depuis Image.tsx (@ts-ignore, non typé côté PictureProps) — React ne le
  // reconnaît pas en camelCase sur un <img> natif ; on le retire du spread et
  // on le repasse en casse HTML minuscule.
  const { fetchPriority, ...imgProps } = props as typeof props & {
    fetchPriority?: string;
  };
  return (
    <img
      {...imgProps}
      {...(fetchPriority
        ? ({ fetchpriority: fetchPriority } as ImgHTMLAttributes<HTMLImageElement>)
        : {})}
      decoding="async"
      loading={loading}
      src={shouldLoad ? src : undefined}
      data-src={!shouldLoad ? src : undefined}
      srcSet={shouldLoad ? srcSet : undefined}
      data-srcset={!shouldLoad ? srcSet : undefined}
      alt={alt}
      style={{ ...style, aspectRatio: aspectRatioCss }}
      draggable={false}
    />
  );
};

export const Picture: React.FC<PictureProps> = function Picture({
  fallback,
  sources = [],
  shouldLoad = true,
  log,
  ...props
}) {
  const sizes = props.sizes || fallback?.sizes;
  const fallbackImage = (
    <Image {...props} {...fallback} sizes={sizes} shouldLoad={shouldLoad} />
  );

  log &&
    console.log("picture", {
      sources,
      fallback,
      shouldLoad,
    });

  if (!sources.length) {
    return fallbackImage;
  }

  return (
    <picture>
      {sources.map(({ media, srcSet, type, sizes: sSizes }) => {
        return (
          <source
            key={`${media}-${type}-${srcSet}`}
            type={type}
            media={media ? media : undefined}
            srcSet={shouldLoad ? srcSet : undefined}
            data-srcset={!shouldLoad ? srcSet : undefined}
            sizes={sSizes || sizes}
          />
        );
      })}
      {fallbackImage}
    </picture>
  );
};
